import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bookmark, Star, Trash2, Plus, X } from "lucide-react";
import { API } from "../lib/helpers";

/**
 * SavedCombosBar — power-user shortcut for saved filter bundles.
 *
 * Renders as a slim horizontal strip above the FilterPanel. Each pill
 * loads a bundle of filters into the parent state (via `onLoad`) and
 * kicks off a search. A "+" button captures the CURRENT filter state
 * (built by the parent via `buildCurrentSnapshot`) and posts it to
 * `/api/filter-combos`.
 *
 * Intentionally minimal — no folders, no per-market scope, no sharing.
 * Ship the smallest useful primitive; expand only if power users
 * actually reach the 25-combo ceiling.
 */
export default function SavedCombosBar({ onLoad, buildCurrentSnapshot, subscription }) {
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState("");

  const isPaid = subscription?.is_paid;

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/filter-combos`);
      setCombos(res.data?.combos || []);
    } catch (e) {
      // 401 = anon user; combos are paid-tier only. Silent no-op.
    }
  };

  useEffect(() => {
    if (isPaid) refresh();
  }, [isPaid]);

  if (!isPaid) return null; // Paid-tier gate

  const handleLoad = async (combo) => {
    if (loading) return;
    setLoading(true);
    try {
      // Best-effort touch — never blocks the actual load.
      axios.post(`${API}/filter-combos/${combo.id}/use`).catch(() => {});
      onLoad(combo.filters);
      toast.success(`Loaded "${combo.name}"`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Give this combo a name first");
      return;
    }
    setSaving(true);
    try {
      const snap = buildCurrentSnapshot();
      const res = await axios.post(`${API}/filter-combos`, { name: name.trim(), filters: snap });
      setCombos((prev) => [res.data, ...prev]);
      setName("");
      setShowSave(false);
      toast.success(`Saved "${res.data.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save combo");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this saved combo?")) return;
    try {
      await axios.delete(`${API}/filter-combos/${id}`);
      setCombos((prev) => prev.filter((c) => c.id !== id));
      toast.success("Combo deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="mb-3" data-testid="saved-combos-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-xs text-muted-foreground uppercase tracking-wider">
          <Bookmark className="w-3 h-3" />
          <span>Saved</span>
        </div>
        {combos.length === 0 && !showSave && (
          <span className="text-[11px] text-muted-foreground/60 italic">No combos yet — save your current filters to jump back later</span>
        )}
        {combos.map((c) => (
          <div key={c.id} className="group inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[11px] bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:border-cyan-500/60 transition-colors">
            <button onClick={() => handleLoad(c)} className="inline-flex items-center gap-1" data-testid={`combo-load-${c.id}`}>
              <Star className="w-3 h-3 fill-cyan-400 text-cyan-400" />
              <span>{c.name}</span>
            </button>
            <button onClick={(e) => handleDelete(c.id, e)} className="opacity-40 group-hover:opacity-100 hover:text-red-400 transition-opacity" title="Delete" data-testid={`combo-delete-${c.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {showSave ? (
          <div className="inline-flex items-center gap-1">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") { setShowSave(false); setName(""); } }}
              placeholder="e.g. Tampa Price Cuts"
              className="text-[11px] px-2 py-1 rounded-full bg-secondary/40 border border-cyan-500/30 focus:outline-none focus:border-cyan-500 text-foreground w-40"
              maxLength={80}
              data-testid="combo-name-input"
            />
            <button onClick={handleSave} disabled={saving} className="text-[11px] px-2 py-1 rounded-full bg-cyan-500 text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="combo-save-confirm">
              {saving ? "…" : "Save"}
            </button>
            <button onClick={() => { setShowSave(false); setName(""); }} className="text-muted-foreground hover:text-foreground" data-testid="combo-save-cancel">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowSave(true)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border border-dashed border-muted-foreground/40 text-muted-foreground hover:text-foreground hover:border-foreground/60 transition-colors"
            data-testid="combo-save-open"
          >
            <Plus className="w-3 h-3" />
            Save current
          </button>
        )}
      </div>
    </div>
  );
}
